/**
 * Authentication Validators
 * 
 * Centralized validation logic for authentication forms
 * to ensure consistency and DRY principle across the application
 */

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export function validateEmail(email) {
  if (!email) {
    return {
      isValid: false,
      error: 'El. paštas yra privalomas'
    };
  }
  
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return {
      isValid: false,
      error: 'Neteisingas el. pašto formatas'
    };
  }
  
  return { isValid: true, error: '' };
}

/**
 * Validate password
 * @param {string} password - Password to validate
 * @param {string} mode - 'login' or 'register' for different validation rules
 * @returns {Object} { isValid: boolean, error: string }
 */
export function validatePassword(password, mode = 'login') {
  if (!password) {
    return {
      isValid: false,
      error: 'Slaptažodis yra privalomas'
    };
  }
  
  // Register mode has stricter requirements
  if (mode === 'register') {
    if (password.length < 8) {
      return {
        isValid: false,
        error: 'Slaptažodis turi būti bent 8 simbolių ilgio'
      };
    }
    
    // Strong password validation - must have lowercase, uppercase, and digit
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return {
        isValid: false,
        error: 'Slaptažodis turi turėti bent vieną mažąją raidę, didžiąją raidę ir skaičių'
      };
    }
  }
  
  return { isValid: true, error: '' };
}

/**
 * Validate password confirmation match
 * @param {string} password - Original password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Object} { isValid: boolean, error: string }
 */
export function validatePasswordMatch(password, confirmPassword) {
  if (!confirmPassword) {
    return {
      isValid: false,
      error: 'Patvirtinkite slaptažodį'
    };
  }
  
  if (password !== confirmPassword) {
    return {
      isValid: false,
      error: 'Slaptažodžiai nesutampa'
    };
  }
  
  return { isValid: true, error: '' };
}

/**
 * Validate login form
 * @param {Object} formData - Form data with email and password
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export function validateLoginForm(formData) {
  const errors = {};
  
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }
  
  const passwordValidation = validatePassword(formData.password, 'login');
  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.error;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validate registration form
 * @param {Object} formData - Form data with email, password, and confirmPassword
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export function validateRegistrationForm(formData) {
  const errors = {};
  
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }
  
  const passwordValidation = validatePassword(formData.password, 'register');
  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.error;
  }
  
  const confirmValidation = validatePasswordMatch(formData.password, formData.confirmPassword);
  if (!confirmValidation.isValid) {
    errors.confirmPassword = confirmValidation.error;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validate forgot password form
 * @param {Object} formData - Form data with email
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export function validateForgotPasswordForm(formData) {
  const errors = {};
  
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validate reset password form
 * @param {Object} formData - Form data with newPassword and confirmPassword
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export function validateResetPasswordForm(formData) {
  const errors = {};
  
  const passwordValidation = validatePassword(formData.newPassword, 'register');
  if (!passwordValidation.isValid) {
    errors.newPassword = passwordValidation.error;
  }
  
  const confirmValidation = validatePasswordMatch(formData.newPassword, formData.confirmPassword);
  if (!confirmValidation.isValid) {
    errors.confirmPassword = confirmValidation.error;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}
